import { BrowserRouter, Navigate, Outlet, Route, Routes } from "react-router-dom";
import { Toaster } from "sonner";
import { AdminProvider, AuthProvider, DraftProvider, useAdminAuth, useAuth } from "@/context/auth";
import { Spinner } from "@/components/kit";
import AppShell from "@/components/AppShell";
import Login from "@/pages/auth/Login";
import Register from "@/pages/auth/Register";
import Forgot from "@/pages/auth/Forgot";
import Home from "@/pages/Home";
import Contacts from "@/pages/Contacts";
import ContactDetail from "@/pages/ContactDetail";
import EditContact from "@/pages/EditContact";
import Scan from "@/pages/Scan";
import ScanReview from "@/pages/ScanReview";
import GuestScan from "@/pages/GuestScan";
import GuestReview from "@/pages/GuestReview";
import { afterAuthPath } from "@/lib/guest";
import Events from "@/pages/Events";
import EventDetail from "@/pages/EventDetail";
import Goals from "@/pages/Goals";
import Notifications from "@/pages/Notifications";
import NotificationSettings from "@/pages/NotificationSettings";
import Profile from "@/pages/Profile";
import EditProfile from "@/pages/EditProfile";
import Subscription from "@/pages/Subscription";
import DigitalCard from "@/pages/DigitalCard";
import DigitalCardEdit from "@/pages/DigitalCardEdit";
import Legal from "@/pages/Legal";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminSubscriptions from "@/pages/admin/AdminSubscriptions";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminBroadcast from "@/pages/admin/AdminBroadcast";
import AdminCategories from "@/pages/admin/AdminCategories";
import AdminGoals from "@/pages/admin/AdminGoals";
import AdminAudit from "@/pages/admin/AdminAudit";

function Private() {
  const { user, loading } = useAuth();
  if (loading) return <Spinner className="min-h-screen items-center" />;
  return user ? <Outlet /> : <Navigate to="/login" replace />;
}
function PublicOnly() {
  const { user, loading } = useAuth();
  if (loading) return <Spinner className="min-h-screen items-center" />;
  return user ? <Navigate to={afterAuthPath()} replace /> : <Outlet />;
}
function AdminGate() {
  const { admin, ready } = useAdminAuth();
  if (!ready) return <Spinner className="min-h-screen items-center" />;
  return admin ? <Outlet /> : <Navigate to="/admin/login" replace />;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AdminProvider>
          <DraftProvider>
            <Routes>
              <Route element={<PublicOnly />}>
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<Forgot />} />
                <Route path="/try" element={<GuestScan />} />
                <Route path="/try/review" element={<GuestReview />} />
              </Route>
              <Route path="/legal/:doc" element={<Legal />} />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={<AdminGate />}>
                <Route element={<AdminLayout />}>
                  <Route index element={<AdminDashboard />} />
                  <Route path="analytics" element={<AdminAnalytics />} />
                  <Route path="users" element={<AdminUsers />} />
                  <Route path="subscriptions" element={<AdminSubscriptions />} />
                  <Route path="settings" element={<AdminSettings />} />
                  <Route path="broadcast" element={<AdminBroadcast />} />
                  <Route path="categories" element={<AdminCategories />} />
                  <Route path="goals" element={<AdminGoals />} />
                  <Route path="audit-logs" element={<AdminAudit />} />
                </Route>
              </Route>
              <Route element={<Private />}>
                <Route element={<AppShell />}>
                  <Route index element={<Home />} />
                  <Route path="contacts" element={<Contacts />} />
                  <Route path="contacts/:id" element={<ContactDetail />} />
                  <Route path="contacts/:id/edit" element={<EditContact />} />
                  <Route path="scan" element={<Scan />} />
                  <Route path="scan/review" element={<ScanReview />} />
                  <Route path="events" element={<Events />} />
                  <Route path="events/:id" element={<EventDetail />} />
                  <Route path="goals" element={<Goals />} />
                  <Route path="notifications" element={<Notifications />} />
                  <Route path="notifications/settings" element={<NotificationSettings />} />
                  <Route path="profile" element={<Profile />} />
                  <Route path="profile/edit" element={<EditProfile />} />
                  <Route path="subscription" element={<Subscription />} />
                  <Route path="dbc" element={<DigitalCard />} />
                  <Route path="dbc/edit" element={<DigitalCardEdit />} />
                </Route>
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <Toaster position="top-center" richColors toastOptions={{ style: { fontFamily: "Inter, sans-serif" } }} />
          </DraftProvider>
        </AdminProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
